/**
 * Right Toolbar Component
 * 
 * A fixed vertical toolbar on the right side of the screen.
 * Contains AI Assistant button and Personalization Brain button.
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import useAuthenticatedAxios from '../../auth/AuthenticatedAxios';
import CustomToolbarActions from './CustomToolbarActions';
import HubAssistantIcon from '../../Images/hubAssistant.svg';
import BrainIcon from '../../Images/brain1.svg';
import BrainGif from '../../Images/brain.gif';

// ============================================
// ANIMATIONS
// ============================================

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 8px rgba(99, 102, 241, 0.3); }
  50% { box-shadow: 0 0 20px rgba(99, 102, 241, 0.6); }
`;

const brainPulse = keyframes`
  0%, 100% { 
    filter: drop-shadow(0 0 6px rgba(147, 51, 234, 0.4));
  }
  50% { 
    filter: drop-shadow(0 0 15px rgba(147, 51, 234, 0.7));
  }
`;

const sparkle = keyframes`
  0%, 100% { opacity: 0.3; }
  50% { opacity: 1; }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(20px); }
  to { opacity: 1; transform: translateX(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

// ============================================
// STYLED COMPONENTS
// ============================================

const ToolbarContainer = styled.div`
  position: fixed;
  right: ${props => props.$isPinned ? '432px' : '16px'};
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  z-index: 998;
  transition: right 0.3s ease;
  /* When there are many actions, scroll the WHOLE toolbar (not a little box
     around the buttons). Cap to the viewport and show a thin scrollbar only
     when needed. overflow-x hidden avoids a stray horizontal bar; per-button
     fly-out menus are portalled so they are never clipped by this scroll. */
  max-height: calc(100vh - 24px);
  overflow-y: auto;
  overflow-x: hidden;
  padding: 6px;
  scrollbar-width: thin;
  scrollbar-color: rgba(99, 102, 241, 0.4) transparent;
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb { background: rgba(99, 102, 241, 0.4); border-radius: 3px; }
  &::-webkit-scrollbar-track { background: transparent; }
`;

const ToolbarButton = styled.button`
  position: relative;
  width: 52px;
  height: 52px;
  border-radius: 14px;
  border: 1px solid ${props => props.$active 
    ? 'rgba(99, 102, 241, 0.5)' 
    : 'rgba(99, 102, 241, 0.2)'};
  background: ${props => props.$active 
    ? 'rgba(99, 102, 241, 0.15)' 
    : 'rgba(20, 25, 40, 0.95)'};
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  
  &:hover {
    border-color: rgba(99, 102, 241, 0.5);
    background: rgba(99, 102, 241, 0.1);
    transform: scale(1.05);
  }
  
  ${props => props.$active && css`
    animation: ${glow} 2s ease-in-out infinite;
  `}
`;

const AIButton = styled(ToolbarButton)`
  img {
    width: 28px;
    height: 28px;
    object-fit: contain;
  }
`;

const BrainButton = styled(ToolbarButton)`
  border-color: ${props => {
    if (props.$active) return 'rgba(147, 51, 234, 0.5)';
    const score = props.$score || 0;
    if (score >= 80) return 'rgba(16, 185, 129, 0.5)'; // Green
    if (score >= 50) return 'rgba(245, 158, 11, 0.5)'; // Yellow
    return 'rgba(239, 68, 68, 0.4)'; // Red
  }};
  box-shadow: ${props => {
    const score = props.$score || 0;
    if (score >= 80) return '0 0 15px rgba(16, 185, 129, 0.3)';
    if (score >= 50) return '0 0 15px rgba(245, 158, 11, 0.3)';
    return '0 0 15px rgba(239, 68, 68, 0.2)';
  }};
  
  img {
    width: 28px;
    height: 28px;
    object-fit: contain;
    filter: brightness(0) invert(1);
    -webkit-filter: brightness(0) invert(1);
  }
`;

const ScoreTooltip = styled.div`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background: ${props => {
    if (props.$score >= 80) return 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
    if (props.$score >= 50) return 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)';
    return 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
  }};
  color: white;
  font-size: 12px;
  font-weight: 700;
  padding: 6px 12px;
  border-radius: 8px;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.2s ease, transform 0.2s ease;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  z-index: 10;
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: ${props => {
      if (props.$score >= 80) return '#059669';
      if (props.$score >= 50) return '#d97706';
      return '#dc2626';
    }};
  }
  
  ${BrainButton}:hover & {
    opacity: 1;
    transform: translateX(-50%) translateY(-4px);
  }
`;

const NotificationDot = styled.div`
  position: absolute;
  top: -4px;
  right: -4px;
  width: 12px;
  height: 12px;
  background: linear-gradient(135deg, #f59e0b, #ef4444);
  border-radius: 50%;
  border: 2px solid #0f1629;
`;

const Tooltip = styled.div`
  position: absolute;
  right: calc(100% + 12px);
  top: 50%;
  transform: translateY(-50%);
  background: rgba(20, 25, 40, 0.98);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.9);
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.2s ease;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  
  ${ToolbarButton}:hover & {
    opacity: 1;
  }
`;

// ============================================
// BRAIN EXPANDED PANEL
// ============================================

const BrainPanel = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 420px;
  max-width: 100vw;
  height: 100vh;
  background: linear-gradient(180deg, #0f1629 0%, #0a0f1a 100%);
  border-left: 1px solid rgba(147, 51, 234, 0.3);
  box-shadow: -8px 0 32px rgba(0, 0, 0, 0.5);
  overflow: hidden;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  animation: ${slideIn} 0.25s ease-out;
`;

const PanelTitleBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: linear-gradient(135deg, rgba(147, 51, 234, 0.15) 0%, rgba(99, 102, 241, 0.1) 100%);
  border-bottom: 1px solid rgba(147, 51, 234, 0.2);
  flex-shrink: 0;
  
  h2 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.95);
    display: flex;
    align-items: center;
    gap: 10px;
    
    img {
      width: 24px;
      height: 24px;
    }
    
    svg {
      width: 24px;
      height: 24px;
    }
  }
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(239, 68, 68, 0.2);
    border-color: rgba(239, 68, 68, 0.3);
    color: #ef4444;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const PanelHeader = styled.div`
  padding: 20px;
  background: linear-gradient(135deg, rgba(10, 14, 26, 0.98) 0%, rgba(14, 18, 32, 0.95) 100%);
  border-bottom: 1px solid rgba(147, 51, 234, 0.2);
  text-align: center;
  flex-shrink: 0;
`;

const BrainVisualization = styled.div`
  position: relative;
  width: 80px;
  height: 80px;
  margin: 0 auto 12px;
`;

const ProgressRing = styled.svg`
  position: absolute;
  top: -10px;
  left: -10px;
  width: 100px;
  height: 100px;
  transform: rotate(-90deg);
  
  circle {
    fill: none;
    stroke-width: 4;
    stroke-linecap: round;
  }
  
  .bg {
    stroke: rgba(147, 51, 234, 0.2);
  }
  
  .progress {
    stroke: url(#progressGradient);
    stroke-dasharray: ${props => props.$circumference};
    stroke-dashoffset: ${props => props.$circumference - (props.$circumference * props.$progress / 100)};
    transition: stroke-dashoffset 1s ease-out;
  }
`;

const ScoreDisplay = styled.div`
  text-align: center;
  
  .score {
    font-size: 28px;
    font-weight: 700;
    background: linear-gradient(135deg, #a78bfa 0%, #818cf8 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  .label {
    font-size: 10px;
    color: rgba(167, 139, 250, 0.7);
    text-transform: uppercase;
    letter-spacing: 1px;
  }
  
  .subtitle {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.35);
    margin-top: 4px;
    line-height: 1.4;
  }
  
  .level {
    margin-top: 4px;
    font-size: 12px;
    font-weight: 600;
    color: ${props => {
      if (props.$level === 'expert') return '#10b981';
      if (props.$level === 'advanced') return '#22c55e';
      if (props.$level === 'intermediate') return '#f59e0b';
      if (props.$level === 'learning') return '#f97316';
      return '#ef4444';
    }};
  }
`;

const SetupInfoTooltipWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: help;
  
  .info-icon {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: rgba(147, 51, 234, 0.2);
    color: rgba(167, 139, 250, 0.6);
    font-size: 10px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 6px;
    transition: all 0.2s;
  }
  
  .tooltip-content {
    display: none;
    position: fixed;
    background: #0f1428;
    border: 1px solid rgba(147, 51, 234, 0.3);
    border-radius: 10px;
    padding: 12px 16px;
    min-width: 260px;
    z-index: 2147483647;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6);
    text-align: left;
    pointer-events: none;
  }
  
  &:hover .info-icon {
    background: rgba(147, 51, 234, 0.4);
    color: #a78bfa;
  }
  
  &:hover .tooltip-content {
    display: block;
  }
  
  .tooltip-title {
    font-size: 12px;
    font-weight: 600;
    color: #a78bfa;
    margin-bottom: 8px;
  }
  
  .tooltip-item {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.6);
    line-height: 1.5;
    margin-bottom: 2px;
  }
`;

const SetupInfoTooltip = ({ children }) => {
  const handleMouseEnter = (e) => {
    const icon = e.currentTarget.querySelector('.info-icon');
    const tooltip = e.currentTarget.querySelector('.tooltip-content');
    if (icon && tooltip) {
      const rect = icon.getBoundingClientRect();
      const tooltipWidth = 260;
      let left = rect.left - tooltipWidth + rect.width;
      if (left < 10) left = 10;
      tooltip.style.top = `${rect.bottom + 8}px`;
      tooltip.style.left = `${left}px`;
    }
  };

  return (
    <SetupInfoTooltipWrapper onMouseEnter={handleMouseEnter}>
      {children}
    </SetupInfoTooltipWrapper>
  );
};

const PanelContent = styled.div`
  padding: 16px;
  flex: 1;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(147, 51, 234, 0.3);
    border-radius: 2px;
  }
`;

const CategorySection = styled.div`
  margin-bottom: 8px;
  background: rgba(147, 51, 234, 0.05);
  border: 1px solid rgba(147, 51, 234, 0.15);
  border-radius: 8px;
  overflow: hidden;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  cursor: pointer;
  transition: background 0.2s ease;
  
  &:hover {
    background: rgba(147, 51, 234, 0.1);
  }
  
  .left {
    display: flex;
    align-items: center;
    gap: 8px;
    
    .expand-icon {
      width: 16px;
      height: 16px;
      color: rgba(167, 139, 250, 0.7);
      transition: transform 0.2s ease;
      transform: ${props => props.$expanded ? 'rotate(90deg)' : 'rotate(0)'};
    }
    
    h4 {
      margin: 0;
      font-size: 11px;
      font-weight: 600;
      color: rgba(255, 255, 255, 0.9);
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
  }
  
  .right {
    display: flex;
    align-items: center;
    gap: 8px;
    
    .count {
      font-size: 11px;
      font-weight: 600;
      color: ${props => props.$score >= 80 ? '#10b981' : props.$score >= 50 ? '#f59e0b' : '#ef4444'};
    }
  }
`;

const CategoryProgress = styled.div`
  height: 3px;
  background: rgba(147, 51, 234, 0.15);
  
  .fill {
    height: 100%;
    background: ${props => props.$score >= 80 
      ? 'linear-gradient(90deg, #10b981 0%, #059669 100%)' 
      : props.$score >= 50 
        ? 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)'
        : 'linear-gradient(90deg, #ef4444 0%, #dc2626 100%)'};
    transition: width 0.5s ease-out;
    width: ${props => props.$progress}%;
  }
`;

const CategoryItems = styled.div`
  max-height: ${props => props.$expanded ? '500px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
  background: rgba(0, 0, 0, 0.2);
`;

const ItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-top: 1px solid rgba(147, 51, 234, 0.1);
  
  .status-icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    color: ${props => props.$completed ? '#10b981' : 'rgba(255, 255, 255, 0.3)'};
  }
  
  .item-info {
    flex: 1;
    min-width: 0;
    
    .name {
      font-size: 11px;
      color: ${props => props.$completed ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.5)'};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    
    .subcategory {
      font-size: 9px;
      color: rgba(147, 51, 234, 0.6);
      margin-top: 1px;
    }
  }
`;

const TipsSection = styled.div`
  padding: 16px;
  background: rgba(147, 51, 234, 0.08);
  border-top: 1px solid rgba(147, 51, 234, 0.15);
  flex: 1;
  min-height: 200px;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(147, 51, 234, 0.3);
    border-radius: 2px;
  }
`;

const TipsTitle = styled.h4`
  margin: 0 0 8px 0;
  font-size: 11px;
  font-weight: 600;
  color: rgba(167, 139, 250, 0.9);
  display: flex;
  align-items: center;
  gap: 5px;
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const TipItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 10px;
  background: rgba(20, 15, 35, 0.5);
  border: 1px solid rgba(147, 51, 234, 0.15);
  border-radius: 6px;
  margin-bottom: 6px;
  animation: ${fadeIn} 0.3s ease-out;
  animation-delay: ${props => props.$index * 0.1}s;
  animation-fill-mode: both;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  .priority {
    flex-shrink: 0;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    margin-top: 4px;
    background: ${props => {
      if (props.$priority === 'high') return '#ef4444';
      if (props.$priority === 'medium') return '#f59e0b';
      return '#22c55e';
    }};
  }
  
  .content {
    flex: 1;
    
    .category {
      font-size: 9px;
      color: rgba(147, 51, 234, 0.7);
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 1px;
    }
    
    .text {
      font-size: 11px;
      color: rgba(255, 255, 255, 0.85);
      line-height: 1.3;
    }
  }
`;

const LoadingState = styled.div`
  padding: 40px;
  text-align: center;
  color: rgba(167, 139, 250, 0.6);
  font-size: 13px;
`;

// ============================================
// PERFECT BRAIN STYLED COMPONENTS
// ============================================

const TabContainer = styled.div`
  display: flex;
  padding: 12px 16px;
  gap: 8px;
  background: rgba(0, 0, 0, 0.2);
  border-bottom: 1px solid rgba(147, 51, 234, 0.15);
`;

const Tab = styled.button`
  flex: 1;
  padding: 10px 16px;
  border-radius: 8px;
  border: 1px solid ${props => props.$active 
    ? 'rgba(147, 51, 234, 0.5)' 
    : 'rgba(255, 255, 255, 0.1)'};
  background: ${props => props.$active 
    ? 'linear-gradient(135deg, rgba(147, 51, 234, 0.2) 0%, rgba(99, 102, 241, 0.15) 100%)' 
    : 'rgba(255, 255, 255, 0.03)'};
  color: ${props => props.$active 
    ? 'rgba(255, 255, 255, 0.95)' 
    : 'rgba(255, 255, 255, 0.5)'};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  
  &:hover {
    background: ${props => props.$active 
      ? 'linear-gradient(135deg, rgba(147, 51, 234, 0.25) 0%, rgba(99, 102, 241, 0.2) 100%)'
      : 'rgba(255, 255, 255, 0.05)'};
    border-color: rgba(147, 51, 234, 0.3);
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const PerfectBrainContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(147, 51, 234, 0.3);
    border-radius: 2px;
  }
`;

const ComponentCard = styled.div`
  background: rgba(20, 15, 35, 0.5);
  border: 1px solid ${props => {
    const score = props.$score || 0;
    if (score >= 80) return 'rgba(16, 185, 129, 0.3)';
    if (score >= 50) return 'rgba(245, 158, 11, 0.3)';
    return 'rgba(239, 68, 68, 0.3)';
  }};
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  &:hover {
    background: rgba(30, 25, 50, 0.7);
  }
`;

const ComponentDetails = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(147, 51, 234, 0.2);
  animation: slideDown 0.2s ease-out;
  
  @keyframes slideDown {
    from {
      opacity: 0;
      max-height: 0;
    }
    to {
      opacity: 1;
      max-height: 200px;
    }
  }
  
  .detail-description {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.75);
    line-height: 1.5;
    margin-bottom: 10px;
  }
  
  .detail-location {
    font-size: 11px;
    color: rgba(147, 51, 234, 0.9);
    background: rgba(147, 51, 234, 0.15);
    padding: 6px 10px;
    border-radius: 6px;
    margin-bottom: 8px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    
    svg {
      width: 12px;
      height: 12px;
    }
  }
  
  .detail-tip {
    font-size: 11px;
    color: rgba(245, 158, 11, 0.9);
    display: flex;
    align-items: flex-start;
    gap: 6px;
    margin-top: 8px;
    
    .tip-icon {
      font-size: 12px;
    }
  }
`;

const ComponentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  
  .name {
    display: flex;
    align-items: center;
    gap: 8px;
    
    .icon {
      font-size: 16px;
    }
    
    .label {
      font-size: 12px;
      font-weight: 600;
      color: rgba(255, 255, 255, 0.9);
    }
  }
  
  .score {
    font-size: 14px;
    font-weight: 700;
    color: ${props => {
      const score = props.$score || 0;
      if (score >= 80) return '#10b981';
      if (score >= 50) return '#f59e0b';
      return '#ef4444';
    }};
  }
`;

const ComponentProgress = styled.div`
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  overflow: hidden;
  
  .fill {
    height: 100%;
    border-radius: 2px;
    transition: width 0.5s ease-out;
    width: ${props => props.$progress}%;
    background: ${props => {
      const score = props.$score || 0;
      if (score >= 80) return 'linear-gradient(90deg, #10b981 0%, #059669 100%)';
      if (score >= 50) return 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)';
      return 'linear-gradient(90deg, #ef4444 0%, #dc2626 100%)';
    }};
  }
`;

const RecommendationsList = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(147, 51, 234, 0.15);
`;

const RecommendationItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  background: rgba(99, 102, 241, 0.08);
  border: 1px solid rgba(99, 102, 241, 0.15);
  border-radius: 8px;
  margin-bottom: 8px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  .icon {
    font-size: 14px;
    flex-shrink: 0;
    margin-top: 2px;
  }
  
  .text {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.8);
    line-height: 1.4;
  }
`;

const OverallScoreCard = styled.div`
  background: linear-gradient(135deg, rgba(147, 51, 234, 0.15) 0%, rgba(99, 102, 241, 0.1) 100%);
  border: 1px solid rgba(147, 51, 234, 0.3);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
  text-align: center;
  
  .score-value {
    font-size: 40px;
    font-weight: 700;
    background: linear-gradient(135deg, #a78bfa 0%, #818cf8 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  .score-label {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.6);
    margin-top: 4px;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
`;

// SVG Icons for Brain Intelligence Components
const BrainIcons = {
  directives: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>,
  flows: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/><polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/><line x1="4" y1="4" x2="9" y2="9"/></svg>,
  rules: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>,
  maps: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg>,
  teachings: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>,
  training: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>,
  strategies: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>,
  knowledge: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>,
  profile: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
};

// Unified Brain Intelligence Components (merged from Perfect Brain + Classic)
const BRAIN_COMPONENTS = [
  { 
    key: 'directivesScore', 
    name: 'Agent Directives', 
    icon: BrainIcons.directives,
    description: 'Core personality and behavior instructions for your AI agent in different modes (Widget, Observer, Summarizer).',
    location: 'Synthora Hub → Agent Directives',
    tip: 'Configure how your AI should behave, respond, and what tone to use in conversations.'
  },
  { 
    key: 'flowsScore', 
    name: 'Scenario Flows', 
    icon: BrainIcons.flows,
    description: 'Step-by-step conversation guides for specific scenarios (consultations, intake, follow-ups).',
    location: 'Synthora Hub → Scenario Flows',
    tip: 'Create flows to guide conversations through specific processes with defined steps.'
  },
  { 
    key: 'rulesScore', 
    name: 'Rule Sets', 
    icon: BrainIcons.rules,
    description: 'Automated rules that trigger specific actions or responses based on conditions.',
    location: 'Synthora Hub → Rule Sets',
    tip: 'Define IF-THEN rules for automatic responses, alerts, or escalations.'
  },
  { 
    key: 'opportunityMapsScore', 
    name: 'Opportunity Maps', 
    icon: BrainIcons.maps,
    description: 'Maps of possible pathways and opportunities your AI can identify for clients.',
    location: 'Synthora Hub → Opportunity Maps',
    tip: 'Create pathways with requirements, alternatives, and discovery questions for opportunity identification.'
  },
  { 
    key: 'strategiesScore', 
    name: 'Expert Strategies', 
    icon: BrainIcons.strategies,
    description: 'Creative workarounds and strategic solutions that capture "what a good expert would do".',
    location: 'Synthora Hub → Expert Strategies',
    tip: 'Document clever solutions, edge cases, and expert insights the AI should apply.'
  },
  { 
    key: 'teachingsScore', 
    name: 'Teachings', 
    icon: BrainIcons.teachings,
    description: 'Corrections and lessons learned from training sessions that improve AI accuracy.',
    location: 'Synthora Hub → Brain Learning → Teachings',
    tip: 'Review and manage teachings created from training sessions to refine AI responses.'
  },
  { 
    key: 'trainingScore', 
    name: 'Training Sessions', 
    icon: BrainIcons.training,
    description: 'Practice sessions where you train the AI by correcting its responses.',
    location: 'Synthora Hub → Training Mode',
    tip: 'Run training sessions to teach your AI through real conversation practice.'
  },
  { 
    key: 'knowledgeCategoriesScore', 
    name: 'Knowledge Categories', 
    icon: BrainIcons.knowledge,
    description: 'Categories that organize your expertise areas. Includes default categories plus any custom ones you create.',
    location: 'Centura Hub → Knowledge Categories',
    tip: 'Score is based on active categories (70%) plus bonus for custom categories you create (30%).'
  },
  { 
    key: 'profileScore', 
    name: 'Profile & Settings', 
    icon: BrainIcons.profile,
    description: 'Your professional profile, firm standards, practice areas, and AI model configurations.',
    location: 'Settings → Profile',
    tip: 'Complete your profile: firm standards, practice area, and customize AI models.'
  },
];

// Keep old name for backward compatibility
const PERFECT_BRAIN_COMPONENTS = BRAIN_COMPONENTS;

// ============================================
// COMPONENT
// ============================================

const RightToolbar = ({ 
  isPanelPinned = false, 
  isAssistantOpen = false, 
  onToggleAssistant,
  hasNewMessages = false 
}) => {
  const authenticatedAxios = useAuthenticatedAxios();
  const [isBrainExpanded, setIsBrainExpanded] = useState(false);
  const [brainData, setBrainData] = useState(null);
  const [perfectBrainData, setPerfectBrainData] = useState(null);
  const [summaryScore, setSummaryScore] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedComponent, setExpandedComponent] = useState(null);
  // Unified brain - no more tabs, always show Perfect Brain

  const toggleCategory = (categoryName) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName]
    }));
  };

  // Load fresh Perfect Brain score on mount - always show actual data
  useEffect(() => {
    const loadFreshScore = async () => {
      try {
        // Fetch fresh Perfect Brain data for accurate score
        const perfectBrainResponse = await authenticatedAxios.get('/api/brain/v2/health');
        
        if (perfectBrainResponse?.data?.data?.overallScore !== undefined) {
          setSummaryScore(perfectBrainResponse.data.data.overallScore);
          setPerfectBrainData(perfectBrainResponse.data.data);
        } else if (perfectBrainResponse?.data?.overallScore !== undefined) {
          // Fallback: if backend returns data directly without wrapper
          setSummaryScore(perfectBrainResponse.data.overallScore);
          setPerfectBrainData(perfectBrainResponse.data);
        } else {
          // Final fallback: use summary endpoint if Perfect Brain not available
          const summaryResponse = await authenticatedAxios.get('/api/personalization/summary');
          setSummaryScore(summaryResponse.data.score);
        }
      } catch (error) {
        console.error('Failed to load brain score:', error);
        // Fallback to summary on error
        try {
          const summaryResponse = await authenticatedAxios.get('/api/personalization/summary');
          setSummaryScore(summaryResponse.data.score);
        } catch (summaryError) {
          console.error('Failed to load fallback summary:', summaryError);
        }
      }
    };
    
    loadFreshScore();
  }, [authenticatedAxios]);

  // Load full data when brain is expanded - ALWAYS recalculate fresh data
  const loadFullData = useCallback(async (forceRefresh = false) => {
    // Skip if we already have data and not forcing refresh
    if (brainData && perfectBrainData && !forceRefresh) {
      return;
    }
    
    setIsLoading(true);
    try {
      // Fetch both classic personalization and Perfect Brain data in parallel
      const [personalizationResponse, perfectBrainResponse] = await Promise.all([
        authenticatedAxios.get('/api/personalization/score?refresh=true'),
        authenticatedAxios.get('/api/brain/v2/health').catch(err => {
          console.log('Perfect Brain V2 not available yet:', err.message);
          return null;
        })
      ]);
      
      // Set classic personalization data
      setBrainData(personalizationResponse.data);
      if (personalizationResponse.data.overallScore !== undefined) {
        setSummaryScore(personalizationResponse.data.overallScore);
      }
      
      // Set Perfect Brain data if available
      // The backend returns { success: true, data: {...} }, so we extract .data.data
      if (perfectBrainResponse?.data?.data) {
        const pbData = perfectBrainResponse.data.data;
        setPerfectBrainData(pbData);
        // Update summaryScore with Perfect Brain score so it persists on hover
        if (pbData.overallScore !== undefined) {
          setSummaryScore(pbData.overallScore);
        }
      } else if (perfectBrainResponse?.data && !perfectBrainResponse.data.success) {
        // Fallback: if backend returns data directly without wrapper
        const pbData = perfectBrainResponse.data;
        setPerfectBrainData(pbData);
        // Update summaryScore with Perfect Brain score so it persists on hover
        if (pbData.overallScore !== undefined) {
          setSummaryScore(pbData.overallScore);
        }
      }
    } catch (error) {
      console.error('Failed to load personalization data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [authenticatedAxios, brainData, perfectBrainData]);

  const handleBrainClick = () => {
    const newExpanded = !isBrainExpanded;
    setIsBrainExpanded(newExpanded);
    if (newExpanded) {
      // Always force refresh when opening to get latest data
      loadFullData(true);
    }
  };

  // Ring circumference for the visualization
  const circumference = 2 * Math.PI * 44;

  return (
    <>
      {/* Toolbar with buttons */}
      <ToolbarContainer $isPinned={isPanelPinned}>
        {/* Brain Intelligence Button */}
        <BrainButton 
          $active={isBrainExpanded} 
          $score={perfectBrainData?.overallScore || summaryScore}
          onClick={handleBrainClick}
        >
          <img 
            src={BrainIcon} 
            alt="Brain Intelligence" 
          />
          {(perfectBrainData?.overallScore || summaryScore) !== null && (
            <ScoreTooltip $score={perfectBrainData?.overallScore || summaryScore}>
              {perfectBrainData?.overallScore || summaryScore}%
            </ScoreTooltip>
          )}
          <Tooltip>Brain Intelligence</Tooltip>
        </BrainButton>

        {/* AI Assistant Button */}
        <AIButton 
          $active={isAssistantOpen} 
          onClick={onToggleAssistant}
          title="Synthora AI Assistant"
        >
          <img src={HubAssistantIcon} alt="AI Assistant" />
          {hasNewMessages && !isAssistantOpen && <NotificationDot />}
          <Tooltip>AI Assistant</Tooltip>
        </AIButton>

        {/* User-defined custom action buttons (+ their add/edit modal) */}
        <CustomToolbarActions />
      </ToolbarContainer>

      {/* Brain Expanded Panel - OUTSIDE ToolbarContainer for proper fixed positioning */}
      {isBrainExpanded && (
        <BrainPanel onClick={(e) => e.stopPropagation()}>
          <PanelTitleBar>
            <h2>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2Z"/>
                <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2Z"/>
              </svg>
              Personalization Brain
            </h2>
            <CloseButton onClick={() => setIsBrainExpanded(false)} title="Close">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </CloseButton>
          </PanelTitleBar>
          <PanelHeader>
            <BrainVisualization>
              <img 
                src={BrainGif} 
                alt="Brain" 
                style={{ 
                  width: '240px', 
                  height: '240px', 
                  objectFit: 'contain',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)'
                }} 
              />
              <ProgressRing 
                $circumference={circumference} 
                $progress={perfectBrainData?.overallScore || summaryScore || 0}
              >
                <defs>
                  <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#9333ea" />
                    <stop offset="100%" stopColor="#6366f1" />
                  </linearGradient>
                </defs>
                <circle className="bg" cx="50" cy="50" r="44" />
                <circle className="progress" cx="50" cy="50" r="44" />
              </ProgressRing>
            </BrainVisualization>
            
            <ScoreDisplay>
              <div className="score">
                {perfectBrainData?.overallScore || summaryScore || 0}%
              </div>
              <div className="label">
                Setup Completeness
                <SetupInfoTooltip>
                  <span className="info-icon">?</span>
                  <div className="tooltip-content">
                    <div className="tooltip-title">Brain Setup Completeness</div>
                    <div className="tooltip-item">Measures how well your AI brain is configured:</div>
                    <div className="tooltip-item">• Agent Directives (12%)</div>
                    <div className="tooltip-item">• Scenario Flows (10%)</div>
                    <div className="tooltip-item">• Rule Sets (10%)</div>
                    <div className="tooltip-item">• Opportunity Maps (15%)</div>
                    <div className="tooltip-item">• Strategies (12%)</div>
                    <div className="tooltip-item">• Teachings (10%)</div>
                    <div className="tooltip-item">• Training Sessions (12%)</div>
                    <div className="tooltip-item">• Knowledge Categories (9%)</div>
                    <div className="tooltip-item">• Profile & Settings (10%)</div>
                  </div>
                </SetupInfoTooltip>
              </div>
              <div className="subtitle">How well your brain is configured</div>
            </ScoreDisplay>
          </PanelHeader>


          {isLoading ? (
            <LoadingState>Analyzing your brain...</LoadingState>
          ) : (
            <PerfectBrainContent>
              {/* Overall Score */}
              <OverallScoreCard>
                <div className="score-value">
                  {perfectBrainData?.overallScore || 0}%
                </div>
                <div className="score-label">Setup Completeness</div>
              </OverallScoreCard>

              {/* Component Cards - Click to expand details */}
              {PERFECT_BRAIN_COMPONENTS.map(component => {
                const score = perfectBrainData?.components?.[component.key] || 0;
                const isExpanded = expandedComponent === component.key;
                return (
                  <ComponentCard 
                    key={component.key} 
                    $score={score}
                    onClick={() => setExpandedComponent(isExpanded ? null : component.key)}
                  >
                    <ComponentHeader $score={score}>
                      <div className="name">
                        <span className="icon">{component.icon}</span>
                        <span className="label">{component.name}</span>
                      </div>
                      <div className="score">{score}%</div>
                    </ComponentHeader>
                    <ComponentProgress $progress={score} $score={score}>
                      <div className="fill" />
                    </ComponentProgress>
                    
                    {/* Expanded Details */}
                    {isExpanded && (
                      <ComponentDetails>
                        <div className="detail-description">
                          {component.description}
                        </div>
                        <div className="detail-location">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                            <circle cx="12" cy="10" r="3"/>
                          </svg>
                          {component.location}
                        </div>
                        <div className="detail-tip">
                          <span className="tip-icon">💡</span>
                          {component.tip}
                        </div>
                      </ComponentDetails>
                    )}
                  </ComponentCard>
                );
              })}

              {/* Recommendations */}
              {perfectBrainData?.recommendations?.length > 0 && (
                <RecommendationsList>
                  <TipsTitle>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 18h6M10 22h4M12 2v1M4.22 4.22l.71.71M1 12h2M4.22 19.78l.71-.71M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10z"/>
                    </svg>
                    Recommendations
                  </TipsTitle>
                  {perfectBrainData.recommendations.map((rec, index) => (
                    <RecommendationItem key={index}>
                      <span className="icon">💡</span>
                      <span className="text">{rec}</span>
                    </RecommendationItem>
                  ))}
                </RecommendationsList>
              )}

              {!perfectBrainData && (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '40px 20px',
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontSize: '13px'
                }}>
                  <div style={{ fontSize: '40px', marginBottom: '16px' }}>🧠</div>
                  <div style={{ marginBottom: '8px', fontWeight: '600' }}>Brain Intelligence</div>
                  <div>Loading brain components...</div>
                </div>
              )}
            </PerfectBrainContent>
          )}
        </BrainPanel>
      )}
    </>
  );
};

export default RightToolbar;

